/**
 * «Связи» on a task (Spec 017): what it waits for, what waits for it and what
 * to read with it. A link informs and notifies; it never stops anyone from
 * taking or closing a task, so the section is information plus two actions.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Link2, OctagonAlert } from "lucide-react";

import { Knowledge, TaskLinks as Links } from "@/api/resources";
import type { LinkedTask, TaskLinkKind, TaskLinksOut, TaskRef } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { TextField } from "@/components/ui/Field";
import { Section } from "@/components/ui/Section";
import { useToast } from "@/components/ui/Toast";

export const taskLinksKey = (taskId: string) => ["task-links", taskId];

const GROUPS: { key: "blocked_by" | "blocks" | "related"; title: string }[] = [
  { key: "blocked_by", title: "Ждёт" },
  { key: "blocks", title: "Блокирует" },
  { key: "related", title: "Связана с" },
];

const KINDS: { kind: TaskLinkKind; label: string }[] = [
  { kind: "blocked_by", label: "ждёт задачу" },
  { kind: "blocks", label: "блокирует задачу" },
  { kind: "relates", label: "связана с задачей" },
];

/** Whose turn it is: the assignee, or nobody yet. */
export function whose(ref: { assignee_id?: string | null; assignee_name?: string | null }): string {
  return ref.assignee_id ? ref.assignee_name ?? "участник" : "никто не взял";
}

/** A linked task's state in words: done, cancelled, or where it is and with whom. */
export function linkState(task: LinkedTask): string {
  if (task.completed_at || task.status_category === "done") return "выполнена";
  if (task.status_category === "cancelled") return "отменена";
  return `${task.status} · ${whose(task)}`;
}

function allLinked(data: TaskLinksOut | undefined): LinkedTask[] {
  return data ? [...data.blocked_by, ...data.blocks, ...data.related] : [];
}

/** The top of the card: what the task waits for. It warns; it does not forbid. */
export function BlockedNotice({ blockers }: { blockers: TaskRef[] }) {
  if (!blockers.length) return null;
  return (
    <Card role="status" className="flex items-start gap-2 border border-dstr/40">
      <OctagonAlert className="mt-0.5 h-[18px] w-[18px] shrink-0 text-dstr" aria-hidden="true" />
      <div className="min-w-0 space-y-1">
        <p className="text-cell font-medium text-text">Задача ждёт другую</p>
        {blockers.map((b) => (
          <Link
            key={b.id}
            to={`/tasks/${b.id}`}
            className="flex min-h-touch items-center break-words text-accent"
          >
            «{b.title}» · {whose(b)}
          </Link>
        ))}
        <p className="text-meta text-hint">
          Взять и закрыть её можно, но сначала стоит договориться с тем, кто держит.
        </p>
      </div>
    </Card>
  );
}

function useRefreshLinks(taskId: string) {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["task-links"] }),
      qc.invalidateQueries({ queryKey: ["task", taskId] }),
      qc.invalidateQueries({ queryKey: ["tasks"] }),
    ]);
}

export function TaskLinks({ taskId }: { taskId: string }) {
  const toast = useToast();
  const refresh = useRefreshLinks(taskId);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<string>();
  const links = useQuery({
    queryKey: taskLinksKey(taskId),
    queryFn: () => Links.list(taskId),
    refetchInterval: 15_000,
  });
  const remove = useMutation({
    mutationFn: (linkId: string) => Links.remove(taskId, linkId),
    onSuccess: async () => {
      setRemoving(undefined);
      await refresh();
      toast.success("Связь снята");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const data = links.data;
  const total = allLinked(data).length;
  return (
    <Section
      title="Связи"
      trailing={total > 0 ? <span className="text-section text-hint">{total}</span> : undefined}
    >
      {links.isError && (
        <p role="alert" className="text-meta text-dstr">
          Не удалось загрузить связи.{" "}
          <Button size="compact" variant="ghost" onClick={() => void links.refetch()}>
            Повторить
          </Button>
        </p>
      )}
      {data &&
        GROUPS.map(({ key, title }) =>
          data[key].length === 0 ? null : (
            <div key={key} className="space-y-1">
              <h3 className="text-meta text-hint">{title}</h3>
              {data[key].map((item) => (
                <Card key={item.link_id} className="flex flex-wrap items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/tasks/${item.id}`}
                      className="flex min-h-touch items-center break-words text-cell text-accent"
                    >
                      {item.title}
                    </Link>
                    <p className="text-meta text-hint">
                      {linkState(item)} · {item.project}
                    </p>
                  </div>
                  {removing === item.link_id ? (
                    <div className="flex gap-1">
                      <Button size="compact" variant="ghost" onClick={() => setRemoving(undefined)}>
                        Отмена
                      </Button>
                      <Button
                        size="compact"
                        variant="destructive-outline"
                        loading={remove.isPending}
                        aria-label={`Точно снять связь с задачей «${item.title}»`}
                        onClick={() => remove.mutate(item.link_id)}
                      >
                        Точно снять
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="compact"
                      variant="ghost"
                      aria-label={`Снять связь с задачей «${item.title}»`}
                      onClick={() => setRemoving(item.link_id)}
                    >
                      Снять
                    </Button>
                  )}
                </Card>
              ))}
            </div>
          ),
        )}
      {data && total === 0 && !adding && (
        <p className="text-meta text-hint">
          Связей нет. Отметь, если задача ждёт другую, блокирует её или связана с ней.
        </p>
      )}
      {adding ? (
        <AddLink taskId={taskId} linked={allLinked(data)} onDone={() => setAdding(false)} />
      ) : (
        <Button size="compact" variant="ghost" onClick={() => setAdding(true)}>
          <Link2 className="h-4 w-4" aria-hidden="true" />
          Добавить связь
        </Button>
      )}
    </Section>
  );
}

function AddLink({
  taskId,
  linked,
  onDone,
}: {
  taskId: string;
  linked: LinkedTask[];
  onDone: () => void;
}) {
  const toast = useToast();
  const refresh = useRefreshLinks(taskId);
  const [kind, setKind] = useState<TaskLinkKind>("blocked_by");
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string>();
  const q = query.trim();
  const found = useQuery({
    queryKey: ["task-link-search", q],
    queryFn: () => Knowledge.search(q, { types: ["task"], limit: 8 }),
    enabled: q.length >= 2,
    staleTime: 30_000,
  });
  const add = useMutation({
    mutationFn: (otherId: string) => Links.add(taskId, kind, otherId),
    onSuccess: async () => {
      await refresh();
      toast.success("Связь добавлена");
      onDone();
    },
    // A cycle or an existing link comes back with a sentence meant for people.
    onError: (e: Error) => setError(e.message),
  });
  const taken = new Set(linked.map((t) => t.id));
  const hits = (found.data ?? []).filter((h) => h.entity_id !== taskId);

  return (
    <Card className="space-y-2">
      <fieldset className="space-y-1">
        <legend className="text-meta text-hint">Эта задача…</legend>
        <div className="flex flex-wrap gap-1">
          {KINDS.map((k) => (
            <Button
              key={k.kind}
              size="compact"
              variant={kind === k.kind ? "secondary" : "ghost"}
              aria-pressed={kind === k.kind}
              onClick={() => {
                setKind(k.kind);
                setError(undefined);
              }}
            >
              {k.label}
            </Button>
          ))}
        </div>
      </fieldset>
      <TextField
        label="Найти задачу"
        value={query}
        placeholder="Название или слова из описания"
        onChange={(e) => {
          setQuery(e.target.value);
          setError(undefined);
        }}
      />
      {found.isFetching && <p className="text-meta text-hint">Ищем…</p>}
      {hits.map((hit) => (
        <Button
          key={hit.entity_id}
          variant="ghost"
          // Titles are long; on a phone they wrap instead of running off the card.
          className="h-auto min-h-touch w-full justify-start whitespace-normal py-2 text-left"
          disabled={add.isPending || taken.has(hit.entity_id)}
          loading={add.isPending && add.variables === hit.entity_id}
          onClick={() => add.mutate(hit.entity_id)}
        >
          {hit.title}
          {hit.reason.status === "completed" ? " · выполнена" : ""}
          {taken.has(hit.entity_id) ? " · уже связана" : ""}
        </Button>
      ))}
      {q.length >= 2 && found.isSuccess && hits.length === 0 && (
        <p className="text-meta text-hint">Ничего не нашлось.</p>
      )}
      {error && (
        <p role="alert" className="text-meta text-dstr">
          {error}
        </p>
      )}
      <Button size="compact" variant="ghost" onClick={onDone}>
        Отмена
      </Button>
    </Card>
  );
}

/** «Связать» under a similar task: one tap makes it «связана с». */
export function LinkSimilar({ taskId, otherId, title }: { taskId: string; otherId: string; title: string }) {
  const toast = useToast();
  const refresh = useRefreshLinks(taskId);
  const links = useQuery({ queryKey: taskLinksKey(taskId), queryFn: () => Links.list(taskId) });
  const add = useMutation({
    mutationFn: () => Links.add(taskId, "relates", otherId),
    onSuccess: async () => {
      await refresh();
      toast.success("Задачи связаны");
    },
    onError: (e: Error) => toast.error(e.message),
  });
  if (allLinked(links.data).some((t) => t.id === otherId)) {
    return (
      <p className="flex min-h-touch items-center gap-1 px-3 text-meta text-hint">
        <Link2 className="h-4 w-4" aria-hidden="true" />
        Уже связана с этой задачей
      </p>
    );
  }
  return (
    <Button
      size="compact"
      variant="ghost"
      className="self-start"
      loading={add.isPending}
      aria-label={`Связать с задачей «${title}»`}
      onClick={() => add.mutate()}
    >
      <Link2 className="h-4 w-4" aria-hidden="true" />
      Связать
    </Button>
  );
}
